export const handleUserTyping = (updateData, onTypingEvent) => {
  if (onTypingEvent) {
    onTypingEvent(updateData);
  }
};

export const handleNewMessage = (updateData, setMessages, currentUser) => {
  if (!updateData.message) return;

  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Check if message already exists by ID
    const existingIndex = currentMessages.findIndex(msg => msg._id === updateData.message._id);
    if (existingIndex !== -1) {
      // Update existing message (for final content)
      const newMessages = [...currentMessages];
      newMessages[existingIndex] = {
        ...newMessages[existingIndex],
        ...updateData.message,
        // Preserve any streaming state if this is an update during streaming
        isStreaming: newMessages[existingIndex].isStreaming || updateData.message.isStreaming
      };
      
      return newMessages;
    }
    
    // Handle message_complete events with generated image data (special case for image generation)
    if (updateData.type === 'message_complete' && updateData.generatedImage && updateData.messageId) {
      const messageIndex = currentMessages.findIndex(msg => msg._id === updateData.messageId);
      if (messageIndex !== -1) {
        const newMessages = [...currentMessages];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          text: '', // Clear any "Creating image" text
          generatedImage: updateData.generatedImage,
          isTemporary: false,
          isComplete: true,
          isStreaming: false
        };
        return newMessages;
      }
    }
    
    // Special handling for user messages - replace temporary user message
    if (updateData.message.sender === 'user') {
      
      // Find the most recent temporary user message with matching text
      let tempUserIndex = -1;
      for (let i = currentMessages.length - 1; i >= 0; i--) {
        const msg = currentMessages[i];
        if (msg.sender === 'user' && 
            msg.isTemporary && 
            msg.text === updateData.message.text) {
          tempUserIndex = i;
          break;
        }
      }
      
      if (tempUserIndex !== -1) {
        
        // Replace the temporary user message with the permanent one
        // Preserve the userId from temporary message since backend doesn't send it
        const tempMessage = currentMessages[tempUserIndex];
        const newMessages = [...currentMessages];
        newMessages[tempUserIndex] = {
          ...updateData.message,
          isTemporary: false, // Make sure it's marked as permanent
          userId: tempMessage.userId // Preserve userId for "Me" identification
        };
        
        console.log('WEBSOCKET: Replaced temporary user message with permanent one', { 
          messageId: updateData.message._id,
          text: updateData.message.text?.substring(0, 50) + '...' 
        });
        return newMessages;
      } else {
        // ENHANCED: Try to find any user message with matching text (even if not temporary)
        let anyUserIndex = -1;
        for (let i = currentMessages.length - 1; i >= 0; i--) {
          const msg = currentMessages[i];
          if (msg.sender === 'user' && msg.text === updateData.message.text) {
            anyUserIndex = i;
            break;
          }
        }
        
        if (anyUserIndex !== -1) {
          // Update existing user message to permanent
          const newMessages = [...currentMessages];
          newMessages[anyUserIndex] = {
            ...newMessages[anyUserIndex],
            ...updateData.message,
            isTemporary: false,
            userId: newMessages[anyUserIndex].userId // Preserve userId
          };
          console.log('WEBSOCKET: Updated existing user message to permanent', { 
            messageId: updateData.message._id 
          });
          return newMessages;
        }
        
        console.warn('WEBSOCKET: No user message found to replace, adding as new message');
        // If we can't find any matching message, add as new
      }
    }
    
    // For new bot messages, check if we should replace a temporary placeholder
    if (updateData.message.sender === 'bot') {
      // If this is a placeholder message (likely [[LOADING_INDICATOR]]), add it for streaming
      if (updateData.message.text === '[[LOADING_INDICATOR]]' || 
          updateData.message.isTemporary) {
        const newMessages = [...currentMessages];
        newMessages.push({
          ...updateData.message,
          isThinking: true,
          isStreaming: false,
          isTemporary: true
        });
        return newMessages;
      }
      
      // For other bot messages, check if we should replace a temporary placeholder
      let tempBotIndex = -1;
      for (let i = currentMessages.length - 1; i >= 0; i--) {
        const msg = currentMessages[i];
        const shouldReplace = msg.sender === 'bot' && 
            (msg.isTemporary || 
             msg.text === '[[LOADING_INDICATOR]]' ||
             msg._id?.startsWith('temp-') ||
             msg._id?.startsWith('placeholder-'));
        
        if (shouldReplace) {
          tempBotIndex = i;
          break;
        }
      }
      
      if (tempBotIndex !== -1) {
        // Replace the temporary message
        const newMessages = [...currentMessages];
        newMessages[tempBotIndex] = updateData.message;
        return newMessages;
      } else {
        console.warn('No temporary bot message found to replace, adding as new message');
      }
    }
    
    // Add new message AT THE END if no replacement was made
    
    const finalMessages = [...currentMessages, updateData.message];
    
    return finalMessages;
  });
};

export const handleContentChunk = (updateData, setMessages, setError, sseStreamingActive) => {
  // Skip content_chunk events if direct streaming is active to prevent duplicates
  if (sseStreamingActive.current) {
    return;
  }
  
  if (!updateData.content) return;
  
  // Clear any existing error since we're receiving content successfully
  setError(null);
  
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Find the LAST bot message that can be updated (temporary, thinking, or streaming)
    let lastBotIndex = -1;
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].sender === 'bot' && 
          (currentMessages[i].isTemporary || 
           currentMessages[i].isThinking || 
           currentMessages[i].isStreaming ||
           currentMessages[i].text === '[[LOADING_INDICATOR]]')) {
        lastBotIndex = i;
        break;
      }
    }
    
    if (lastBotIndex === -1) {
      console.warn('No updateable bot message found, looking for any recent bot message');
      
      // As a fallback, find the most recent bot message (within last 30 seconds)
      const now = Date.now();
      for (let i = currentMessages.length - 1; i >= 0; i--) {
        if (currentMessages[i].sender === 'bot' && 
            currentMessages[i].timestamp &&
            (now - new Date(currentMessages[i].timestamp).getTime()) < 30000) {
          lastBotIndex = i;
          break;
        }
      }
    }
    
    if (lastBotIndex === -1) {
      console.warn('Still no bot message found, creating new one for orphaned content chunk');
      
      // If no existing message found, create a new one for the content
      const newBotMessage = {
        _id: `temp-streaming-${Date.now()}`,
        text: updateData.content,
        sender: 'bot',
        timestamp: new Date(),
        isTemporary: false,
        isStreaming: true,
        isThinking: false
      };
      
      return [...currentMessages, newBotMessage];
    }
    
    const newMessages = [...currentMessages];
    const currentMsg = newMessages[lastBotIndex];
    
    // Replace placeholder text or append content
    if (currentMsg.text === '[[LOADING_INDICATOR]]' || 
        currentMsg.text === 'Generating response...' ||
        currentMsg.text === 'Querying SuperBucket...' ||
        !currentMsg.text || 
        currentMsg.text.trim() === '' ||
        updateData.replace === true) {
      newMessages[lastBotIndex] = {
        ...currentMsg,
        text: updateData.content,
        isThinking: false,
        isStreaming: true,
        isTemporary: false
      };
    } else {
      newMessages[lastBotIndex] = {
        ...currentMsg,
        text: currentMsg.text + updateData.content,
        isThinking: false,
        isStreaming: true,
        isTemporary: false
      };
    }
    
    return newMessages;
  });
};

export const handleStreamingComplete = (setMessages, setError, setIsLoading, sseStreamingActive, chatId, data) => {
  // Skip WebSocket streaming complete if we're currently in direct streaming mode
  if (sseStreamingActive.current) {
    return;
  }
  
  // Handle regeneration completion
  if (data?.isRegeneration && data?.messageId) {
    
    // Fetch the updated message from the backend to get the proper responses array
    const processRegenerationCompletion = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${data.messageId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const updatedMessage = await response.json();
          
          // Update the message with the backend's responses array
          setMessages(prev => prev.map(msg => 
            msg._id === data.messageId ? {
              ...updatedMessage,
              isStreaming: false,
              isTemporary: false,
              isRegenerating: false,
              isComplete: true,
              // Show the text from the active response
              text: updatedMessage.responses?.[updatedMessage.activeResponseIndex]?.text || updatedMessage.text
            } : msg
          ));
          
          // Stop loading
          setIsLoading(false);
        } else {
          console.error('Failed to fetch updated message:', response.status);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error processing regeneration completion:', error);
        setIsLoading(false);
      }
    };
    
    processRegenerationCompletion();
    return; // Always return for regeneration
  }
  
  // Clear any existing errors since streaming completed successfully
  setError(null);
  
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Find the LAST streaming bot message
    let lastStreamingIndex = -1;
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].sender === 'bot' && 
          (currentMessages[i].isStreaming || currentMessages[i].isTemporary || 
           currentMessages[i].isAnalyzing)) {
        lastStreamingIndex = i;
        break;
      }
    }
    
    if (lastStreamingIndex === -1) {
      console.warn('No streaming message found to mark as complete');
      return currentMessages;
    }
    
    const newMessages = [...currentMessages];
    const finalMessage = newMessages[lastStreamingIndex];
    
    // Message marked as complete
    
    // Don't show fallback error if we were analyzing documents
    // The SSE stream might still be processing the content
    if (finalMessage.isAnalyzing || finalMessage.analysisStatus) {
      return currentMessages; // Don't modify the message, let SSE handle it
    }
    
    // If the message has no content, provide a fallback
    if (!finalMessage.text || finalMessage.text.trim() === '' || 
        finalMessage.text === '[[LOADING_INDICATOR]]') {
      // Message completed with no content, providing fallback
      newMessages[lastStreamingIndex] = {
        ...finalMessage,
        text: "I apologize, but I'm having trouble generating a response. Please try again.",
        isStreaming: false,
        isTemporary: false,
        isThinking: false,
        isError: true
      };
    } else {
      newMessages[lastStreamingIndex] = {
        ...finalMessage,
        isStreaming: false,
        isTemporary: false,
        isThinking: false
      };
    }
    
    return newMessages;
  });
  
  setIsLoading(false);

  // After streaming is complete, check if chat title needs updating
  if (chatId) {
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/${chatId}/preview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const chatData = await response.json();
          const newTitle = chatData.chat?.title;
          
          if (newTitle) {
            // Dispatch event to update header title
            window.dispatchEvent(new CustomEvent('chatTitleUpdated', {
              detail: { chatId, newTitle }
            }));
          }
        }
      } catch (error) {
        console.error('Error checking for title update:', error);
      }
    }, 2000); // Wait 2 seconds for title generation to complete
  }
};

export const handleDocumentStatus = (updateData, setMessages) => {
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Find the most recent bot message or create one
    let lastBotIndex = -1;
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].sender === 'bot' && 
          (currentMessages[i].isTemporary || currentMessages[i].isThinking || 
           currentMessages[i].isStreaming || currentMessages[i].isAnalyzing)) {
        lastBotIndex = i;
        break;
      }
    }
    
    if (lastBotIndex === -1) {
      // Create new bot message with analysis status
      const newBotMessage = {
        _id: `temp-analysis-${Date.now()}`,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isAnalyzing: updateData.status === 'analyzing' || updateData.status === 'processing',
        analysisStatus: updateData.message || 'Processing document...',
        analysisProgress: updateData.progress || 0,
        isThinking: false,
        isTemporary: true
      };
      
      return [...currentMessages, newBotMessage];
    } else {
      // Update existing bot message
      const newMessages = [...currentMessages];
      newMessages[lastBotIndex] = {
        ...newMessages[lastBotIndex],
        isAnalyzing: updateData.status === 'analyzing' || updateData.status === 'processing',
        analysisStatus: updateData.message || 'Processing document...',
        analysisProgress: updateData.progress || 0,
        isThinking: false
      };
      
      return newMessages;
    }
  });
};

export const handleTitleUpdated = (data, updateData) => {
  // Dispatch a chatTitleUpdated event for the Sidebar component to pick up
  window.dispatchEvent(new CustomEvent('chatTitleUpdated', {
    detail: {
      chatId: data.chatId,
      title: updateData.title
    }
  }));
};

export const handleMessageComplete = (updateData, setMessages) => {
  const { messageId, generatedImage, generatedFile, text: completeText } = updateData;
  
  if (!messageId) {
    console.warn('handleMessageComplete: No messageId provided');
    return;
  }
  
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    return currentMessages.map(msg => {
      if (msg._id === messageId) {
        // Use backend text when provided (e.g. file gen shortcuts stream), else clear loading indicator
        const finalText = completeText != null
          ? completeText
          : (msg.text === '[[LOADING_INDICATOR]]' ? '' : msg.text);
        
        const updatedMessage = {
          ...msg,
          isTemporary: false,
          isThinking: false,
          isStreaming: false,
          isComplete: true,
          isGeneratingImage: false,
          isSearching: false,
          text: finalText
        };
        
        if (generatedImage) {
          updatedMessage.generatedImage = generatedImage;
        }
        if (generatedFile) {
          updatedMessage.generatedFile = generatedFile;
        }
        
        return updatedMessage;
      }
      return msg;
    });
  });
};

export const handleSearchComplete = (updateData, setMessages) => {
  const { messageId, searchResults, searchSources } = updateData;
  
  if (!messageId) {
    console.warn('handleSearchComplete: No messageId provided');
    return;
  }
  
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    return currentMessages.map(msg => {
      if (msg._id === messageId) {
        
        const updatedMessage = {
          ...msg,
          isSearching: false,
          hasWebSearch: true,
          searchStatus: 'Search complete',
          isComplete: false, // Keep as incomplete since text response will follow
          isStreaming: true // Keep streaming for the text response
        };
        
        // Add search results if provided
        if (searchResults && Array.isArray(searchResults)) {
          updatedMessage.searchResults = searchResults;
        }
        
        // Add search sources if provided (for favicon display)
        if (searchSources && Array.isArray(searchSources)) {
          updatedMessage.searchSources = searchSources;
        }
        
        return updatedMessage;
      }
      return msg;
    });
  });
};

export const handleToolCallStart = (updateData, setMessages, toolCallManager) => {
  console.log('handleToolCallStart called with:', updateData);
  
  // Find the current bot message being streamed
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Find the most recent bot message that's streaming
    let messageIndex = -1;
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].sender === 'bot' && 
          (currentMessages[i].isStreaming || currentMessages[i].isTemporary)) {
        messageIndex = i;
        break;
      }
    }
    
    if (messageIndex !== -1) {
      const messageId = currentMessages[messageIndex]._id;
      
      // Add tool call to the manager
      if (toolCallManager) {
        toolCallManager.handleToolCallStart(messageId, updateData);
      }
      
      // Mark message as having active tool calls
      const newMessages = [...currentMessages];
      newMessages[messageIndex] = {
        ...newMessages[messageIndex],
        hasToolCalls: true,
        toolCallsActive: true
      };
      
      return newMessages;
    }
    
    return currentMessages;
  });
};

export const handleToolCallResult = (updateData, setMessages, toolCallManager) => {
  console.log('handleToolCallResult called with:', updateData);
  
  // Find the message with active tool calls
  setMessages(prev => {
    const currentMessages = Array.isArray(prev) ? prev : [];
    
    // Find the most recent bot message with active tool calls
    let messageIndex = -1;
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].sender === 'bot' && currentMessages[i].hasToolCalls) {
        messageIndex = i;
        break;
      }
    }
    
    if (messageIndex !== -1) {
      const messageId = currentMessages[messageIndex]._id;
      
      // Update tool call result in the manager
      if (toolCallManager) {
        toolCallManager.handleToolCallResult(messageId, updateData);
      }
      
      // Check if all tool calls are complete
      const stats = toolCallManager?.getToolCallStats(messageId) || {};
      const allComplete = stats.executing === 0;
      
      if (allComplete) {
        const newMessages = [...currentMessages];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          toolCallsActive: false
        };
        return newMessages;
      }
    }
    
    return currentMessages;
  });
};